/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { TicketContainer, TICKET_CHANGE_EVENTS } from '../tickets/TicketContainer';
import { TICKET_ACTIVITY_NAME_SETTINGS } from './TICKET_TEXT_SETTINGS';
import { Ticket, Side } from '@touchplan/common';
import { TicketUtils } from '@appCanvasCommon/utils/TicketUtils';
import { TicketTextCanvas } from './TicketTextCanvas';
import { FancyTextStyle } from '@appCanvasCommon/interfaces/fancy-text-style.interface';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { TicketType } from '@touchplan/common';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { setTicketWidthHelper } from 'src/app/utils/ticket-utils';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';

export class TicketActivityNameCanvas extends TicketTextCanvas {
	public maskContainer: FancyContainer = new FancyContainer();
	public maskGraphics: FancyGraphics = new FancyGraphics();

	private _lastWidth: number;
	private _unsubUpdate: any;
	private canvasAngularLand: CanvasAngularLand;
	constructor(
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer,
		text: string = '',
		style: FancyTextStyle = new FancyTextStyle({ wordWrap: true, fill: '#ffffff' }),
		canvas?: HTMLCanvasElement
	) {
		super(text, style, canvasAngularLand, ticketContainer, canvas);
		this.canvasAngularLand = canvasAngularLand;
		this.maskContainer.mask = this.maskGraphics;
		this.maskContainer.addChild(this);
		this.eventMode = EventMode.None;
		ticketContainer.ABOVE.addChild(this.maskGraphics);

		ticketContainer.on(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);
		// this._unsubUpdate = ticketContainer.camera.changed$.subscribe(this.update);

		this.onChanged(ticketContainer);
	}

	public update = () => {
		if (!this.ticket.visible || !this.ticket.renderable) {
			return;
		}
		this.onChanged(this.ticket);
	};

	public override destroy(options?): void {
		this.ticket.off(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);
		// this._unsubUpdate.unsubscribe();
		super.destroy(options);
	}

	/**
	 * Aligns the ticket activity name for both the active and pull sides
	 * From spec:
	 * - ACTIVE:
	 * -- fontSize 32px if durationDays === 1
	 * -- fontSize: 42px if durationDays > 1
	 * -- font-weight: 500
	 * -- line-height: 32px
	 * -- Multi-line Text align left
	 * - PULL:
	 * -- fontSize: 32px
	 * -- lineHeight: 36px
	 * -- fontWeight: 500
	 * -- Multi-line Text Align center
	 * -- Align text to center of container.
	 * @param {Ticket} data The ticket model
	 */
	private _alignTask(data: Ticket): void {
		const side: Side = data.view.side;
		const userTextScale = this._getTextScale();
		if (side === Side.ACTIVE) {
			this.align.x = false;
			this.align.y = false;
			this.style.fontSize = (data.view.liveDurationDays > 1 ? 42 : 32) * userTextScale;
			this.style.lineHeight = 32 * userTextScale;
			this.style.align = 'left';
		} else if (side === Side.PULL) {
			this.align.x = true;
			this.align.y = true;
			this.style.align = 'center';
			this.style.fontSize = 32 * userTextScale;
			this.style.lineHeight = 36 * userTextScale;
		}
	}
	/**
	 * Align the ticket activity name for milestones on both the active and pull side
	 * From Spec:
	 * - ACTIVE:
	 * -- fontSize: 32px
	 * -- fontWeight: 500
	 * -- lineHeight: 32px
	 * -- Multi-line Text aligned center
	 * - PULL:
	 * -- fontSize: 36px
	 * -- fontWeight: 500
	 * -- lineHeight: 36px
	 * -- Multi-line Text aligned center
	 * -- Align to center of container
	 * @param {Ticket} data
	 */
	private _alignMilestone(data: Ticket): void {
		const side: Side = data.view.side;
		const userTextScale = this._getTextScale();
		if (side === Side.ACTIVE) {
			this.align.x = true;
			this.align.y = true;
			this.style.fontSize = 32 * userTextScale;
			this.style.lineHeight = 32 * userTextScale;
			this.style.align = 'center';
		} else if (side === Side.PULL) {
			this.align.x = true;
			this.align.y = true;
			this.style.align = 'center';
			this.style.fontSize = 32 * userTextScale;
			this.style.lineHeight = 36 * userTextScale;
		}
	}
	/**
	 * This aligns the constraint ticket description text for both the active and pull sides
	 * From spec:
	 * - ACTIVE:
	 * -- fontSize: 32px
	 * -- fontWeight: 500
	 * -- lineHeight: 32px
	 * -- Multi-line Text aligned left
	 * - PULL:
	 * -- fontSize: 32px
	 * -- fontWeight: 500
	 * -- lineHeight: 36;
	 * -- Multi-line Text aligned center
	 * -- Align to center of container.
	 * @param {Ticket} data
	 */
	private _alignConstraint(data: Ticket): void {
		const side: Side = data.view.side;
		const userTextScale = this._getTextScale();
		if (side === Side.ACTIVE) {
			this.align.x = true;
			this.align.y = true;
			this.style.align = 'left';
			this.style.fontSize = 32 * userTextScale;
			this.style.lineHeight = 32 * userTextScale;
		} else if (side === Side.PULL) {
			this.align.x = true;
			this.align.y = true;
			this.style.align = 'center';
			this.style.fontSize = 32 * userTextScale;
			this.style.lineHeight = 36 * userTextScale;
		}
	}

	private _handleSide = () => {
		const data = this.ticket.getData();
		const type = TicketUtils.getTicketType(data);

		// We could simplify this A LOT we have common elements...but MOCA...Might be best to keep the alignment rules separate for the inevitable changes.
		// Simplify once things stabilize a bit...
		switch (type) {
			case TicketType.Constraint:
				this._alignConstraint(data);
				break;
			case TicketType.Milestone:
				this._alignMilestone(data);
				break;
			default:
				this._alignTask(data);
				break;
		}

		const offset: number = this._getHeightOffset(data);
		this.maskArea.y += offset;
		this.maskArea.height -= offset;
		this.x = this.maskArea.x;
		this.y = this.maskArea.y;
	};

	private onChanged = (ticketContainer: TicketContainer) => {
		if (!ticketContainer.renderable || !ticketContainer.visible) {
			return;
		}

		this.tint = this.ticket.liveRoleTextColor;

		this.applyOffsetAndDrawArea(this.ticket, TICKET_ACTIVITY_NAME_SETTINGS);

		this._handleSide();

		const data = ticketContainer.getData();
		const layout = ticketContainer.getLayout();
		this.setText(data.view.shortActivityName);

		this.positionCenter(data);

		// Draw text mask
		this.maskGraphics.clear();
		this.maskGraphics.beginFill(0x000000, 1);
		// We add the offset (this.x) to the mask width when drawing the mask for MOC-2863.
		// Wordwrap library is shifting over the text slightly but is not factoring in the offset when drawing. As a result, some characters are sliced in half from the mask.
		this.maskGraphics.drawRect(this.maskArea.x, this.maskArea.y, this.maskArea.width, this.maskArea.height);
		this.maskGraphics.endFill();

		this._lastWidth = setTicketWidthHelper(data, layout, this.canvasAngularLand.wideTicketSize$.getValue());

		// MAKE LESS VISIBLE
		if (data.view.isActualized) {
			this.alpha = 0.5;
		} else {
			this.alpha = 1.0;
		}
	};

	private _getTextScale(): number {
		return this.canvasAngularLand.userPreferenceStateService.canvasTextScale || 1;
	}
}
